import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'default' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const VARIANT: Record<string, string> = {
  primary: 'rgx-btn-primary font-bold',
  default: 'rgx-btn-primary font-bold',
  secondary:
    'bg-surface text-text border border-border font-semibold hover:bg-surface-2',
  outline:
    'bg-surface text-text border border-border font-semibold hover:bg-surface-2',
  ghost:
    'text-primary-text font-semibold hover:bg-primary-soft',
  destructive:
    'bg-danger text-white font-bold hover:bg-danger/90 disabled:bg-border disabled:text-text-3 disabled:hover:bg-border',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-[18px] text-sm',
  lg: 'h-10 px-[18px] text-sm',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, disabled, children, ...props }, ref) => {
    const isDisabled = disabled || loading;
    return (
      <button
        ref={ref}
        disabled={isDisabled}
        data-loading={loading ? 'true' : undefined}
        className={cn(
          'relative inline-flex items-center justify-center gap-2 rounded-md transition-colors',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary',
          VARIANT[variant],
          SIZE[size],
          disabled && !loading && 'cursor-not-allowed bg-border text-text-3 hover:bg-border hover:text-text-3',
          loading && 'cursor-wait',
          className
        )}
        {...props}
      >
        <span className={cn('inline-flex items-center justify-center gap-2', loading && 'invisible')}>
          {children}
        </span>
        {loading && (
          <svg
            className="absolute h-4 w-4 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
export type { ButtonProps };
